import React, { useMemo } from 'react';
import classes from './PostCardMediaPreviewLinkedin.module.scss';
import { SocialProfileWithBoards } from '../../../../hooks/useCategoriesProfilesWithBoards';
import { PostCardPreviewMediaDataType } from '../PostCardMediaPreview/PostCardMediaPreview.view';
import {
  MediaType,
  Network,
  PostFeedType,
  PostMultiMediaAttachMode,
  SocialOAuthProvider
} from '../../../../models/graphql';
import { serializeText, serializeTextToHtml } from '../../TextEditor/helpers/serializer';
import classNames from 'classnames';
import { SocialProfile } from '../../../../components/socialProfiles/SocialProfile';
import { SocialNetworksEnum } from '../../../../constants/social-networks';
import { Loader } from '../../../../components/containers/Loader';
import { FacebookLinkPreviewCard } from '../FacebookLinkPreviewCard';
import { Icon } from '../../../../components/images/Icon/Icon';
import { IconsEnum } from '../../../../constants/icons';
import { FirstCommentPreview } from '../FirstCommentPreview';
import { canAddComment } from '../../helpers/canAddComment';
import { MediaTypePostEditorState } from '../../../../models/post-editor';
import { SingularValidator } from '../../validators/helpers/singular-validator';
import { InvalidMediaSet } from '../PostCardMediaPreview/InvalidMediaSet';

export type PostCardMediaPreviewLinkedinProfile = {
  socialProfile: SocialProfileWithBoards & { __typename: 'LinkedInSocialProfile' };
  title?: string;
};

export type PostCardMediaPreviewLinkedinViewProps = PostCardPreviewMediaDataType & PostCardMediaPreviewLinkedinProfile;

export const isInvalidLinkedInMedia = (media: MediaTypePostEditorState[]): boolean =>
  SingularValidator.noMixedMediaInvalid(media, PostMultiMediaAttachMode.MULTIPLE) ||
  SingularValidator.linkedinMediaLimitInvalid(media, PostMultiMediaAttachMode.MULTIPLE);

export const PostCardMediaPreviewLinkedinView = (props: PostCardMediaPreviewLinkedinViewProps): JSX.Element => {
  const { text, media, socialProfile, link, title, firstComment } = props;
  const loadingLinkMetadata = link && link.linkMetadata === undefined;
  const linkMetadata = link?.linkMetadata;
  const serializedText = serializeText(text);
  const hasText = !!serializedText || !!title;

  const getMedia = useMemo(() => {
    if (!media.length) return <></>;
    if (isInvalidLinkedInMedia(media))
      return (
        <div className={classNames(classes.MediaWrapper, hasText ? classes.MediaWrapperHasText : null)}>
          <InvalidMediaSet className={classNames(classes.MediaItem, classes.MediaInvalid)} />
        </div>
      );
    const baseMediaClasses = media.length > 1 ? classes.MediaItem : classes.MediaItemSolo;
    let computedMediaItemClasses = '';
    const getClasses = (index: number, mediaLength: number): string => {
      switch (mediaLength) {
        case 1:
          computedMediaItemClasses = classNames(baseMediaClasses);
          break;
        case 2:
          computedMediaItemClasses = classNames(baseMediaClasses, classes.MediaTallHalf, {
            [classes.TopLeft]: index === 0,
            [classes.TopRight]: index === 1
          });
          break;
        case 3:
          computedMediaItemClasses = classNames(
            baseMediaClasses,
            index === 0 ? classes.MediaTopFull : classes.MediaWideHalf,
            {
              [classes.TopLeft]: index === 0,
              [classes.BottomLeft]: index === 1,
              [classes.BottomRight]: index === 2
            }
          );
          break;
        case 4:
          computedMediaItemClasses = classNames(
            baseMediaClasses,
            index === 0 ? classes.MediaTopFull : classes.MediaWideThird,
            {
              [classes.TopLeft]: index === 0,
              [classes.BottomLeft]: index === 1,
              [classes.BottomCentre]: index === 2,
              [classes.BottomRight]: index === 3
            }
          );
          break;
        default:
          computedMediaItemClasses = classNames(
            baseMediaClasses,
            index < 2 ? classes.MediaTopHalf : classes.MediaWideThird,
            {
              [classes.TopLeft]: index === 0,
              [classes.TopRight]: index === 1,
              [classes.BottomLeft]: index === 2,
              [classes.BottomCentre]: index === 3,
              [classes.BottomRight]: index === 4
            }
          );
          break;
      }
      return computedMediaItemClasses;
    };
    return (
      <>
        <div
          className={classNames(classes.MediaWrapper, hasText ? classes.MediaWrapperHasText : null, {
            [classes.MediaWrapperMultipleImages]: media.length > 1
          })}>
          {media
            .filter((_, index) => index < 5)
            .map((mediaItem, index) => {
              return (
                <div className={getClasses(index, media.length)} key={`media-item-${index}`}>
                  <img
                    src={mediaItem.type === MediaType.VIDEO ? mediaItem.thumbnailUrl : mediaItem.url}
                    alt={`linkedin preview ${index + 1}`}
                  />
                  {index === 4 && media.length > 5 ? (
                    <div className={classes.MoreThanFiveOverlay}>
                      <h2 className={classes.MoreThanFiveText}>+{media.length - 5}</h2>
                    </div>
                  ) : null}
                  {media[index].type === MediaType.VIDEO ? (
                    <div className={classes.MediaMarker}>
                      <Icon name={IconsEnum.play} size={'large'} />
                    </div>
                  ) : null}
                  {media[index].gif ? (
                    <div className={classes.GifMarker}>
                      <Icon name={IconsEnum.gifBadge} size={'large'} />
                    </div>
                  ) : null}
                </div>
              );
            })}
        </div>
      </>
    );
  }, [media, hasText]);

  return (
    <>
      <div className={classes.SocialProfileWrapper}>
        <SocialProfile
          socialProfile={{
            id: socialProfile.id,
            provider: SocialOAuthProvider.LINKEDIN,
            network: Network.LINKEDIN,
            name: socialProfile.displayName,
            type: socialProfile.type
          }}
          disconnected={!socialProfile.connected}
          sublabel={true}
          size={'medium'}
          label={true}
          image={socialProfile.photoUrl}
        />
      </div>
      {media.length ? getMedia : null}
      {title ? <div className={classes.TextTitle}>{title}</div> : null}
      {serializedText ? (
        <div
          className={classes.TextWrapper}
          dangerouslySetInnerHTML={{ __html: serializeTextToHtml(text, SocialNetworksEnum.linkedin) }}></div>
      ) : null}
      {(loadingLinkMetadata || linkMetadata) && !media.length && link ? (
        <div className={classNames(classes.LinkPreviewWrapper, classes.LinkPreviewWrapperLarge)}>
          {/* ^^ TODO hardcoded since we still don't have image size from the link preview */}
          {loadingLinkMetadata ? <Loader variation={'dark'} size={'small'} type={'inline'} /> : null}
          {linkMetadata && !link.removePreview ? (
            <FacebookLinkPreviewCard
              link={link.url}
              size={'large'} // TODO hardcoded since we still don't have image size from the link preview
              description={linkMetadata.openGraph?.description || ''}
              title={linkMetadata.openGraph?.title || ''}
              image={linkMetadata.openGraph?.image || ''}
              __typename={'OpenGraph'}
            />
          ) : null}
        </div>
      ) : null}
      <div className={classNames(classes.FooterWrapper, 'grid-x', 'align-justify')}>
        <div className={'cell shrink'}>
          <div className={classNames('grid-x', 'align-center', 'align-middle', classes.FooterItem)}>
            <Icon name={IconsEnum.thumbUp} size={'medium'} />
            <div className={classes.FooterItemText}>Like</div>
          </div>
        </div>
        <div className={'cell shrink'}>
          <div className={classNames('grid-x', 'align-center', 'align-middle', classes.FooterItem)}>
            <Icon name={IconsEnum.comments} size={'medium'} />
            <div className={classes.FooterItemText}>Comment</div>
          </div>
        </div>
        <div className={'cell shrink'}>
          <div className={classNames('grid-x', 'align-center', 'align-middle', classes.FooterItem)}>
            <Icon name={IconsEnum.share} size={'medium'} />
            <div className={classes.FooterItemText}>Share</div>
          </div>
        </div>
        <div className={'cell shrink'}>
          <div className={classNames('grid-x', 'align-center', 'align-middle', classes.FooterItem)}>
            <Icon name={IconsEnum.igSend} size={'medium'} />
            <div className={classes.FooterItemText}>Send</div>
          </div>
        </div>
      </div>
      {firstComment && canAddComment([socialProfile], PostFeedType.FEED) ? (
        <FirstCommentPreview firstComment={firstComment} socialProfile={socialProfile} />
      ) : null}
    </>
  );
};
